/* Shared PO-seed builder.
 *
 * The Low-Stock and Out-of-Stock reports let an operator select reorder
 * candidates and hand them to the PO drawer (PurchaseOrder.jsx) as prefilled
 * lines. Both feed `buildPoSeedLines`, which:
 *   - maps a source item to the drawer's line-entry shape (see `blankEntry()`
 *     in PurchaseOrder.jsx), and
 *   - aggregates duplicates by medId (a medicine can appear in several low-stock
 *     batches, or across several stuck orders) — summing the order quantity so
 *     the PO carries one line per medicine.
 *
 * Keeping this here (not in either screen) keeps the two entry points in sync
 * and the report components thin. */

const num = (v) => Number(v) || 0;

/* One source item -> drawer line-entry shape. Missing fields default to ""
   (blank price/qty) so the operator fills them in the drawer.
   `allocations` (out-of-stock reorders only) is carried through untouched so the
   PO payload can trace each line back to the stuck orders it fulfils. */
const toLine = (it = {}) => {
  const medId = it.medId || it.productCode || it.drugCode || "";
  return {
    medId,
    productCode: medId,
    productName: it.productName || it.medicineName || it.drugName || "",
    manufacturer: it.manufacturer || "",
    quantity: it.quantity != null && it.quantity !== "" ? num(it.quantity) : "",
    free: it.free != null && it.free !== "" ? num(it.free) : "",
    purchasePrice: it.purchasePrice != null && it.purchasePrice !== "" ? num(it.purchasePrice) : "",
    mrp: it.mrp != null && it.mrp !== "" ? num(it.mrp) : "",
    salePrice: it.salePrice != null && it.salePrice !== "" ? num(it.salePrice) : "",
    discountPct: it.discountPct != null && it.discountPct !== "" ? num(it.discountPct) : "",
    taxPct: it.taxPct != null && it.taxPct !== "" ? num(it.taxPct) : "",
    allocations: Array.isArray(it.allocations) ? [...it.allocations] : [],
  };
};

/* Build deduped PO seed lines from a list of source items. Duplicate medIds are
   merged: quantities (and free qty) sum, allocations concatenate; the first
   line's prices/name win. */
export const buildPoSeedLines = (items = []) => {
  const byMed = new Map();
  (items || []).forEach((raw) => {
    const line = toLine(raw);
    if (!line.medId) return;
    const existing = byMed.get(line.medId);
    if (!existing) {
      byMed.set(line.medId, line);
      return;
    }
    existing.quantity = num(existing.quantity) + num(line.quantity);
    existing.free = num(existing.free) + num(line.free);
    existing.allocations = [...(existing.allocations || []), ...(line.allocations || [])];
    /* Backfill name/prices if the first occurrence lacked them. */
    if (!existing.productName) existing.productName = line.productName;
    if (!existing.manufacturer) existing.manufacturer = line.manufacturer;
    if (!existing.purchasePrice) existing.purchasePrice = line.purchasePrice;
    if (!existing.mrp) existing.mrp = line.mrp;
    if (!existing.salePrice) existing.salePrice = line.salePrice;
    if (!existing.taxPct) existing.taxPct = line.taxPct;
  });
  return Array.from(byMed.values());
};

export default buildPoSeedLines;
